import React, { useEffect, useState } from 'react';

import { Button } from '@/design-system/button';
import { Input, InputRoot } from '@/design-system/input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/design-system/popover';
import { SearchSelect } from '@/design-system/search-select';
import {
  SegmentedControl,
  SegmentedItem,
} from '@/design-system/segmented-control';
import { toast } from '@/design-system/toaster';
import {
  ExportScanPreset,
  InvoiceExportError,
  InvoiceExportFormat,
  InvoiceExportParams,
  InvoiceExportStatus,
  INVOICE_STATUS_LABELS,
  invoicesAPI,
} from '@/services/api/invoices';
import { useSearchableOptions } from './useSearchableOptions';

const PRESETS: { key: ExportScanPreset; label: string }[] = [
  { key: 'today', label: 'Today' },
  { key: 'yesterday', label: 'Yesterday' },
  { key: 'last_7_days', label: 'Last 7 days' },
  { key: 'last_30_days', label: 'Last 30 days' },
];

// Every invoice status is selectable; labels come from the shared status map so
// they stay consistent with the rest of the app.
const STATUS_OPTIONS: { key: InvoiceExportStatus; label: string }[] = (
  [
    'RECEIVED_BY_RIDER',
    'RECEIVED_AT_HUB',
    'WAITING_CONFIRMATION',
    'DISPUTED',
    'RESOLVED',
    'EXCEPTION',
  ] as InvoiceExportStatus[]
).map((key) => ({ key, label: INVOICE_STATUS_LABELS[key] }));

interface ExportPopoverProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const ExportPopover: React.FC<ExportPopoverProps> = ({
  open,
  onOpenChange,
}) => {
  const [preset, setPreset] = useState<ExportScanPreset | ''>('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [issuePreset, setIssuePreset] = useState<ExportScanPreset | ''>('');
  const [issueFrom, setIssueFrom] = useState('');
  const [issueTo, setIssueTo] = useState('');
  const [clientId, setClientId] = useState('');
  const [outletId, setOutletId] = useState('');
  const [hubId, setHubId] = useState('');
  const [statuses, setStatuses] = useState<InvoiceExportStatus[]>([]);
  const [format, setFormat] = useState<InvoiceExportFormat>('csv');
  const [submitting, setSubmitting] = useState(false);
  const [inlineError, setInlineError] = useState<string | null>(null);

  const clients = useSearchableOptions(open, invoicesAPI.clients);
  const outlets = useSearchableOptions(open, invoicesAPI.outlets);
  const hubs = useSearchableOptions(open, invoicesAPI.hubs);

  const optionsError = clients.error ?? outlets.error ?? hubs.error;

  useEffect(() => {
    if (!open) return;
    setPreset('');
    setFrom('');
    setTo('');
    setIssuePreset('');
    setIssueFrom('');
    setIssueTo('');
    setClientId('');
    setOutletId('');
    setHubId('');
    setStatuses([]);
    setFormat('csv');
    setInlineError(null);
    setSubmitting(false);
  }, [open]);

  // Click-outside + Escape are handled by <Popover>. We only need to gate
  // closing while an export request is in-flight.
  const safeClose = () => {
    if (submitting) return;
    onOpenChange(false);
  };

  const choosePreset = (next: ExportScanPreset) => {
    setPreset(next);
    setFrom('');
    setTo('');
    setInlineError(null);
  };

  const updateFrom = (value: string) => {
    setFrom(value);
    setPreset('');
    setInlineError(null);
  };

  const updateTo = (value: string) => {
    setTo(value);
    setPreset('');
    setInlineError(null);
  };

  const chooseIssuePreset = (next: ExportScanPreset) => {
    setIssuePreset(next);
    setIssueFrom('');
    setIssueTo('');
    setInlineError(null);
  };

  const updateIssueFrom = (value: string) => {
    setIssueFrom(value);
    setIssuePreset('');
    setInlineError(null);
  };

  const updateIssueTo = (value: string) => {
    setIssueTo(value);
    setIssuePreset('');
    setInlineError(null);
  };

  const toggleStatus = (key: InvoiceExportStatus) => {
    setInlineError(null);
    setStatuses((prev) =>
      prev.includes(key) ? prev.filter((s) => s !== key) : [...prev, key]
    );
  };

  const hasCustomRange = Boolean(from && to);
  const hasIssueRange = Boolean(issueFrom || issueTo);
  const hasIssueFilter = Boolean(issuePreset) || hasIssueRange;
  const hasDateFilter = Boolean(preset) || hasCustomRange || hasIssueFilter;
  const canSubmit = !submitting && hasDateFilter && statuses.length > 0;
  const rangeInverted = hasCustomRange && from > to;
  const issueRangeInverted =
    Boolean(issueFrom) && Boolean(issueTo) && issueFrom > issueTo;

  const handleDownload = async () => {
    setInlineError(null);
    if (rangeInverted || issueRangeInverted) {
      setInlineError('The "to" date must be on or after the "from" date.');
      return;
    }
    if ((from && !to) || (!from && to)) {
      setInlineError('Pick both scan dates, or use a preset.');
      return;
    }
    if (!hasDateFilter) {
      setInlineError('Pick a scan-date or issue-date filter.');
      return;
    }
    if (statuses.length === 0) {
      setInlineError('Pick at least one status.');
      return;
    }
    const params: InvoiceExportParams = { format, status: statuses };
    if (preset) params.scannedPreset = preset;
    if (hasCustomRange) {
      params.scannedFrom = from;
      params.scannedTo = to;
    }
    if (issuePreset) params.issueDatePreset = issuePreset;
    if (issueFrom) params.issueDateFrom = issueFrom;
    if (issueTo) params.issueDateTo = issueTo;
    if (clientId) params.clientId = clientId;
    if (outletId) params.outletId = outletId;
    if (hubId) params.hubId = hubId;

    setSubmitting(true);
    try {
      await invoicesAPI.export(params);
      toast.success('Export ready', {
        description:
          format === 'xlsx'
            ? 'Your XLSX is downloading.'
            : 'Your CSV is downloading.',
      });
      onOpenChange(false);
    } catch (err) {
      // Surface every failure (including 400s like empty results) as a toast
      // carrying the backend message, rather than a quiet inline banner.
      let message = 'Something went wrong. Please try again.';
      if (err instanceof InvoiceExportError) {
        if (err.code === 'EXPORT_LIMIT_EXCEEDED') {
          const n = err.count ? err.count.toLocaleString() : 'Too many';
          message = `${n} rows match. Narrow the date range or pick a single client / recipient / hub (max 50,000).`;
        } else if (err.code === 'EXPORT_EMPTY') {
          message = 'No invoices match these filters in this range.';
        } else {
          message = err.message;
        }
      } else if (err instanceof Error) {
        message = err.message;
      }
      toast.error('Export failed', { description: message });
    } finally {
      setSubmitting(false);
    }
  };

  const labelCls =
    'mb-2 text-[10px] font-bold uppercase tracking-[0.18em] text-text-sub-600';

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        if (submitting && !next) return;
        onOpenChange(next);
      }}
    >
      <PopoverTrigger asChild>
        <Button tone="neutral" style="stroke">
          <i className="ri-download-line mr-1.5 text-[14px]" />
          Export
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-[400px] p-0"
        aria-label="Export received invoices"
        onInteractOutside={(e) => {
          if (submitting) {
            e.preventDefault();
            return;
          }
          const t = e.target as HTMLElement | null;
          if (t?.closest?.('[data-searchable-dropdown-portal]')) {
            e.preventDefault();
          }
        }}
      >
        <div className="border-b border-stroke-soft-200 px-4 py-3">
          <p className="text-sm font-semibold tracking-tight text-text-strong-950">
            Export invoices
          </p>
          <p className="mt-0.5 text-[12px] text-text-sub-600">
            Scanned surat jalan for payment reconciliation.
          </p>
        </div>
        <div className="space-y-4 px-4 py-3 max-h-[70vh] overflow-y-auto">
          <div>
            <p className={labelCls}>Scan date</p>
            <div className="flex flex-wrap gap-1">
              {PRESETS.map((p) => {
                const active = preset === p.key;
                return (
                  <button
                    key={p.key}
                    type="button"
                    disabled={submitting}
                    onClick={() => choosePreset(p.key)}
                    className={`rounded-lg border px-2.5 py-1 text-xs font-semibold tracking-tight transition-colors disabled:opacity-50 ${
                      active
                        ? 'border-accent text-accent'
                        : 'border-stroke-soft-200 bg-bg-white-0 text-text-sub-600 hover:bg-bg-weak-50'
                    }`}
                  >
                    {p.label}
                  </button>
                );
              })}
            </div>
            <div className="mt-2 flex items-center gap-2">
              <InputRoot size="sm" className="flex-1">
                <Input
                  type="date"
                  value={from}
                  disabled={submitting}
                  onChange={(e) => updateFrom(e.target.value)}
                />
              </InputRoot>
              <span className="text-xs text-text-soft-400">to</span>
              <InputRoot size="sm" className="flex-1">
                <Input
                  type="date"
                  value={to}
                  disabled={submitting}
                  onChange={(e) => updateTo(e.target.value)}
                />
              </InputRoot>
            </div>
            <p className="mt-1.5 text-[11px] text-text-soft-400">
              Dates are in Asia/Jakarta.
            </p>
          </div>

          <div>
            <p className={labelCls}>Issue date</p>
            <div className="flex flex-wrap gap-1">
              {PRESETS.map((p) => {
                const active = issuePreset === p.key;
                return (
                  <button
                    key={p.key}
                    type="button"
                    disabled={submitting}
                    onClick={() => chooseIssuePreset(p.key)}
                    className={`rounded-lg border px-2.5 py-1 text-xs font-semibold tracking-tight transition-colors disabled:opacity-50 ${
                      active
                        ? 'border-accent text-accent'
                        : 'border-stroke-soft-200 bg-bg-white-0 text-text-sub-600 hover:bg-bg-weak-50'
                    }`}
                  >
                    {p.label}
                  </button>
                );
              })}
            </div>
            <div className="mt-2 flex items-center gap-2">
              <InputRoot size="sm" className="flex-1">
                <Input
                  type="date"
                  value={issueFrom}
                  disabled={submitting}
                  onChange={(e) => updateIssueFrom(e.target.value)}
                />
              </InputRoot>
              <span className="text-xs text-text-soft-400">to</span>
              <InputRoot size="sm" className="flex-1">
                <Input
                  type="date"
                  value={issueTo}
                  disabled={submitting}
                  onChange={(e) => updateIssueTo(e.target.value)}
                />
              </InputRoot>
            </div>
            <p className="mt-1.5 text-[11px] text-text-soft-400">
              Filter by the invoice's issue date. Leave one side open for an
              on-or-after / on-or-before bound.
            </p>
          </div>

          <div>
            <p className={labelCls}>Status</p>
            <div className="flex flex-wrap gap-1.5">
              {STATUS_OPTIONS.map((opt) => {
                const active = statuses.includes(opt.key);
                return (
                  <button
                    key={opt.key}
                    type="button"
                    disabled={submitting}
                    onClick={() => toggleStatus(opt.key)}
                    className={`inline-flex items-center gap-1 rounded-lg border px-2.5 py-1 text-xs font-semibold tracking-tight transition-colors disabled:opacity-50 ${
                      active
                        ? 'border-accent text-accent'
                        : 'border-stroke-soft-200 bg-bg-white-0 text-text-sub-600 hover:bg-bg-weak-50'
                    }`}
                    aria-pressed={active}
                  >
                    {active && <i className="ri-check-line text-[12px]" />}
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="grid grid-cols-1 gap-3">
            <div>
              <label className={labelCls + ' block'}>Client</label>
              <SearchSelect
                placeholder="All clients"
                options={clients.options.map((c) => ({
                  value: c.id,
                  label: c.name,
                }))}
                value={clientId}
                selectedLabelFallback={clients.labelFor(clientId)}
                onChange={(next) => setClientId(next)}
                onSearchChange={clients.setSearch}
                disableLocalFilter
                loading={clients.loading}
                disabled={submitting}
                emptyMessage={clients.error ?? 'No clients found'}
              />
            </div>

            <div>
              <label className={labelCls + ' block'}>Recipient</label>
              <SearchSelect
                placeholder="All recipients"
                options={outlets.options.map((o) => ({
                  value: o.id,
                  label: o.name,
                }))}
                value={outletId}
                selectedLabelFallback={outlets.labelFor(outletId)}
                onChange={(next) => setOutletId(next)}
                onSearchChange={outlets.setSearch}
                disableLocalFilter
                loading={outlets.loading}
                disabled={submitting}
                emptyMessage={outlets.error ?? 'No recipients found'}
              />
            </div>

            <div>
              <label className={labelCls + ' block'}>Hub</label>
              <SearchSelect
                placeholder="All hubs"
                options={hubs.options.map((h) => ({
                  value: h.id,
                  label: h.name,
                }))}
                value={hubId}
                selectedLabelFallback={hubs.labelFor(hubId)}
                onChange={(next) => setHubId(next)}
                onSearchChange={hubs.setSearch}
                disableLocalFilter
                loading={hubs.loading}
                disabled={submitting}
                emptyMessage={hubs.error ?? 'No hubs found'}
              />
            </div>
          </div>

          <div>
            <p className={labelCls}>Format</p>
            <SegmentedControl
              value={format}
              onValueChange={(v: string) => {
                if (v) {
                  setFormat(v as InvoiceExportFormat);
                  setInlineError(null);
                }
              }}
            >
              {(['csv', 'xlsx'] as InvoiceExportFormat[]).map((f) => (
                <SegmentedItem key={f} value={f} disabled={submitting}>
                  {f.toUpperCase()}
                </SegmentedItem>
              ))}
            </SegmentedControl>
          </div>

          {optionsError && (
            <p className="text-[11px] text-error-base">{optionsError}</p>
          )}

          {inlineError && (
            <div className="rounded-lg border border-error-light bg-error-lighter px-3 py-2 text-xs text-error-base">
              {inlineError}
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-stroke-soft-200 px-4 py-3">
          <Button
            tone="neutral"
            style="stroke"
            size="sm"
            onClick={safeClose}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={handleDownload}
            disabled={!canSubmit}
            loading={submitting}
          >
            {!submitting && (
              <i className="ri-download-line mr-1.5 text-[14px]" />
            )}
            {submitting ? 'Preparing…' : `Download ${format.toUpperCase()}`}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default ExportPopover;
